import { Injectable } from "@angular/core";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, tap, withLatestFrom } from "rxjs/operators";
import { SubmissionsFacade } from "../../../../../../../domain/+state/submissions.facade";
import { DownloadRequirementDocumentData, DeleteRequirementDocumentData, SaveRequirementDocumentData } from "../../../../../../../domain/models/submissions.model";
import { SubmissionDetailsActionsService } from "../../../../../actions/submission-details-actions.service";
import { RequirementData } from "../../../../../shared/components/requirement-documents-section/requirement-documents-panel.model";
import { AutosaveResponseData } from "../../../form-details.model";
import { FormDetailsActionsService } from "../../../services/actions/form-details-actions.service";
import { AutosaveFormDetailsFacade } from "../../../services/autosave/autosave-form-details.facade";
import { ApplicationData } from "../../../../../../../domain/models/application.model";

@UntilDestroy()
@Injectable()
export class DocumentsDetailsService {
  constructor(
    private readonly submissionDetailsActions: SubmissionDetailsActionsService,
    private readonly formDetailsActions: FormDetailsActionsService,
    private readonly submissionsFacade: SubmissionsFacade,
    private readonly autosaveFormDetailsFacade: AutosaveFormDetailsFacade,
  ) { }

  initDocumentsActions(application: ApplicationData): void {
    this.submissionDetailsActions.downloadRequirementDocument$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.formDetailsActions.autosaveResponse$)
      )
      .subscribe(([downloadData, autosaveResponseData]: [DownloadRequirementDocumentData, AutosaveResponseData]) => {
        this.downloadApplicationDocument(autosaveResponseData.params.applicationId, downloadData);
      })

    this.submissionDetailsActions.saveRequirementDocuments$
      .pipe(untilDestroyed(this))
      .subscribe((saveData: SaveRequirementDocumentData[]) => {
        this.saveDocuments(saveData, application);
      })

    this.submissionDetailsActions.deleteRequirementDocument$
      .pipe(untilDestroyed(this))
      .subscribe((deleteData: DeleteRequirementDocumentData) => {
        this.deleteDocument(deleteData, application);
      })
  }

  private downloadApplicationDocument(applicationId: number, data: DownloadRequirementDocumentData): void {
    this.submissionsFacade.downloadApplicationDocument(applicationId, data.documentId, data.filename, true)
      .pipe(finalize(() => this.hideRequirementsLoader([data?.requirementId]))
      ).subscribe();
  }

  private saveDocuments(saveData: SaveRequirementDocumentData[], application: ApplicationData): void {
    const touchedRequirementIds = Array.from(new Set(saveData?.map(item => item.requirementId)));
    this.autosaveFormDetailsFacade.saveDocuments(saveData, application)
      .pipe(tap((refreshedRequirements: RequirementData[]) => this.refreshRequirements(refreshedRequirements, touchedRequirementIds)))
      .subscribe();
  }

  private deleteDocument(deleteData: DeleteRequirementDocumentData, application: ApplicationData): void {
    const touchedRequirementIds = [deleteData.requirementId];
    this.autosaveFormDetailsFacade.deleteDocument(deleteData, application)
      .pipe(tap((refreshedRequirement: RequirementData) => this.refreshRequirements([refreshedRequirement], touchedRequirementIds)))
      .subscribe();
  }

  private refreshRequirements(requirements: RequirementData[], touchedRequirementIds: number[]): void {
    if (!requirements?.length) {
      this.hideRequirementsLoader(touchedRequirementIds);
      return
    }

    requirements.forEach(requirement => {
      this.updateDocumentsData(requirement, true);
    });
  }

  private hideRequirementsLoader(requirementIds: number[]): void {
    if (!requirementIds?.length) {
      return;
    }

    requirementIds.forEach(requirementId => {
      this.updateDocumentsData({ requirementId, isLoading: false });
    })
  }

  private updateDocumentsData(requirementChanges: Partial<RequirementData>, hasAnyDocumentChanges: boolean = false): void {
    this.formDetailsActions.updateDocumentsData({ requirementChanges, hasAnyDocumentChanges });
  }
}
